import {InlineLink, PricingOptions, iconColors, Text} from '@primer/react-brand/esm'
import {InfoIcon} from '@primer/octicons-react'

import {BLOCKS, INLINES, MARKS} from '@contentful/rich-text-types'
import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import {documentToPlainTextString} from '@contentful/rich-text-plain-text-renderer'

import type {
  PrimerComponentPricingOptions,
  PrimerComponentPricingOptionsFeatureListGroupHeading,
  PrimerComponentPricingOptionsFeatureListHeading,
  PrimerComponentPricingOptionsFeatureListItem,
} from '../../../schemas/contentful/contentTypes/primerComponentPricingOptions'
import type {RichText} from '../../../schemas/contentful/richText'

import {getAnalyticsEvent} from '../../../lib/utils/analytics'
import {ContentfulInlineFootnote} from '../ContentfulFootnotes/ContentfulInlineFootnote'
import {getPrimerIcon} from '../../../lib/utils/icons'
import {getInfoTooltipAriaLabel} from './get-info-tooltip-aria-label'

export type ContentfulPricingOptionsProps = {
  component: PrimerComponentPricingOptions
}

function renderActionsMessage(richText: RichText, context: string) {
  return documentToReactComponents(richText, {
    renderMark: {
      [MARKS.BOLD]: text => (
        <Text as="strong" size="100" weight="semibold">
          {text}
        </Text>
      ),
    },
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_, children) => (
        <Text as="p" size="100">
          {children}
        </Text>
      ),
      [INLINES.HYPERLINK]: (node, children) => (
        <InlineLink
          href={node.data.uri}
          {...getAnalyticsEvent({
            action: documentToPlainTextString(node, ' '),
            tag: 'link',
            context,
            location: 'pricing_options_actions_message',
          })}
        >
          {children}
        </InlineLink>
      ),
    },
  })
}

const leadingVisualFillMap: Record<string, string> = {
  ...Object.fromEntries(iconColors.map(color => [color, `--brand-Icon-color-${color}`])),
  primary: '--brand-color-accent-primary',
  muted: '--brand-color-accent-muted',
}

export function ContentfulPricingOptions({component}: ContentfulPricingOptionsProps) {
  const {align, variant, items} = component.fields

  return (
    <PricingOptions variant={variant} align={align}>
      {items.map(({sys, fields}, i) => {
        const {
          label,
          heading,
          headingLevel,
          description,
          currencyCode,
          currentPrice,
          currencySymbol,
          originalPrice,
          priceTrailingText,
          accordionHeadingLevel,
          featureList,
          featureListExpanded,
          featureListHasDivider,
          callToActionPrimary,
          callToActionPrimaryVariant,
          callToActionSecondary,
          callToActionSecondaryVariant,
          actionsMessage,
          hideCtas = false,
          footnote,
        } = fields

        return (
          <PricingOptions.Item key={sys.id}>
            {label && <PricingOptions.Label>{label.fields.text}</PricingOptions.Label>}

            {heading && (
              <PricingOptions.Heading as={headingLevel}>{documentToPlainTextString(heading)}</PricingOptions.Heading>
            )}

            {description && (
              <PricingOptions.Description>{documentToPlainTextString(description)}</PricingOptions.Description>
            )}

            <PricingOptions.Price
              currencyCode={currencyCode}
              currencySymbol={currencySymbol}
              originalPrice={originalPrice}
              trailingText={priceTrailingText}
            >
              {documentToPlainTextString(currentPrice)}
            </PricingOptions.Price>

            {callToActionPrimary && !hideCtas && (
              <PricingOptions.PrimaryAction
                as="a"
                href={callToActionPrimary.fields.href}
                variant={
                  callToActionPrimaryVariant === 'accent' ? 'primary' : (callToActionPrimaryVariant ?? 'primary')
                }
                {...getAnalyticsEvent(
                  {
                    action: callToActionPrimary.fields.text,
                    tag: 'button',
                    context: 'CTAs',
                    location: `pricing_options_${i + 1}`,
                  },
                  {context: false},
                )}
              >
                {callToActionPrimary.fields.text}
              </PricingOptions.PrimaryAction>
            )}

            {callToActionSecondary && !hideCtas && (
              <PricingOptions.SecondaryAction
                as="a"
                href={callToActionSecondary.fields.href}
                variant={callToActionSecondaryVariant ?? 'subtle'}
                {...getAnalyticsEvent(
                  {
                    action: callToActionSecondary.fields.text,
                    tag: 'button',
                    context: 'CTAs',
                    location: `pricing_options_${i + 1}`,
                  },
                  {context: false},
                )}
              >
                {callToActionSecondary.fields.text}
              </PricingOptions.SecondaryAction>
            )}

            {actionsMessage ? (
              <PricingOptions.ActionsMessage leadingComponent={<InfoIcon />}>
                {renderActionsMessage(actionsMessage, documentToPlainTextString(heading))}
              </PricingOptions.ActionsMessage>
            ) : null}

            {featureList && featureList.length > 0 && (
              <PricingOptions.FeatureList
                expanded={featureListExpanded}
                hasDivider={featureListHasDivider}
                accordionAs={accordionHeadingLevel}
              >
                {featureList.map(feature => {
                  if (feature.sys.contentType.sys.id === 'primerComponentPricingItemListHeading') {
                    const featureListHeading = feature as PrimerComponentPricingOptionsFeatureListHeading
                    return (
                      <PricingOptions.FeatureListHeading key={feature.sys.id}>
                        {documentToPlainTextString(featureListHeading.fields.heading)}
                      </PricingOptions.FeatureListHeading>
                    )
                  }

                  if (feature.sys.contentType.sys.id === 'primerComponentPricingOptionsListHeading') {
                    const featureListGroupHeading = feature as PrimerComponentPricingOptionsFeatureListGroupHeading
                    const featureListGroupHeadingLevel = featureListGroupHeading.fields.headingLevel

                    // Only spread the `as` prop if a heading level is defined;
                    // passing `as={undefined}` causes the incorrect heading level to be rendered.
                    // PB Issue: https://github.com/primer/brand/issues/1007
                    const props = {
                      ...(featureListGroupHeadingLevel && {
                        as: featureListGroupHeadingLevel,
                      }),
                    }

                    return (
                      <PricingOptions.FeatureListGroupHeading key={feature.sys.id} {...props}>
                        {documentToPlainTextString(featureListGroupHeading.fields.heading)}
                      </PricingOptions.FeatureListGroupHeading>
                    )
                  }

                  const featureListItem = feature as PrimerComponentPricingOptionsFeatureListItem
                  const Octicon = getPrimerIcon(featureListItem.fields.icon)

                  const leadingVisualFill =
                    featureListItem.fields.iconColor && leadingVisualFillMap[featureListItem.fields.iconColor]
                  return (
                    <PricingOptions.FeatureListItem
                      key={feature.sys.id}
                      variant={featureListItem.fields.variant}
                      leadingVisual={Octicon}
                      leadingVisualFill={leadingVisualFill ? `var(${leadingVisualFill})` : undefined}
                      infoTooltip={featureListItem.fields.infoTooltip}
                      infoTooltipAriaLabel={
                        featureListItem.fields.infoTooltipAriaLabel ??
                        getInfoTooltipAriaLabel(featureListItem.fields.description)
                      }
                    >
                      {documentToReactComponents(featureListItem.fields.description, {
                        renderNode: {
                          [BLOCKS.PARAGRAPH]: (_, children) => {
                            return children
                          },
                          [INLINES.EMBEDDED_ENTRY]: node => {
                            if (node.data.target.sys.contentType.sys.id === 'inlineFootnote') {
                              return (
                                <ContentfulInlineFootnote
                                  component={node.data.target}
                                  analyticsLocation={`pricing_options_${i + 1}`}
                                />
                              )
                            } else if (node.data.target.sys.contentType.sys.id === 'link') {
                              const {text, href, openInNewTab} = node.data.target.fields
                              return (
                                <InlineLink
                                  href={href}
                                  target={openInNewTab ? '_blank' : undefined}
                                  {...getAnalyticsEvent({
                                    action: text,
                                    tag: 'hyperlink',
                                    context: documentToPlainTextString(featureListItem.fields.description),
                                    location: `pricing_options_${i + 1}`,
                                  })}
                                >
                                  {text}
                                </InlineLink>
                              )
                            }

                            return null
                          },
                        },
                        renderMark: {
                          [MARKS.BOLD]: text => <strong>{text}</strong>,
                        },
                      })}
                    </PricingOptions.FeatureListItem>
                  )
                })}
              </PricingOptions.FeatureList>
            )}

            {footnote && (
              <PricingOptions.Footnote>
                {documentToReactComponents(footnote, {
                  renderNode: {
                    [BLOCKS.PARAGRAPH]: (_, children) => children,
                    [INLINES.EMBEDDED_ENTRY]: node => {
                      if (node.data.target.sys.contentType.sys.id === 'link') {
                        const {text, href, openInNewTab} = node.data.target.fields
                        return (
                          <InlineLink
                            href={href}
                            target={openInNewTab ? '_blank' : undefined}
                            {...getAnalyticsEvent({
                              action: text,
                              tag: 'hyperlink',
                              context: 'footnotes',
                              location: `pricing_options_${i + 1}`,
                            })}
                          >
                            {text}
                          </InlineLink>
                        )
                      }
                    },
                  },
                })}
              </PricingOptions.Footnote>
            )}
          </PricingOptions.Item>
        )
      })}
    </PricingOptions>
  )
}
